import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../context/AuthContext";
import { Lock, Mail, Loader2, ArrowLeft } from "lucide-react";

const Login = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from?.pathname || "/";

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setIsSubmitting(true);

        const result = await login(email, password);

        if (result.success) {
            navigate(from, { replace: true });
        } else {
            setError(result.message);
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            <button
                onClick={() => navigate('/landing')}
                className="absolute top-6 left-6 md:top-10 md:left-10 z-10 flex items-center group transition-all"
            >
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full flex items-center justify-center shadow-xl shadow-gray-200/50 group-hover:shadow-indigo-100 group-hover:scale-110 transition-all border border-gray-100">
                    <ArrowLeft className="text-gray-400 group-hover:text-indigo-600 group-hover:-translate-x-1 transition-all" size={20} />
                </div>
                <span className="hidden sm:block ml-3 text-[10px] md:text-xs font-black text-gray-400 group-hover:text-indigo-600 uppercase tracking-[0.2em] transition-colors">{t('common.back')}</span>
            </button>
            <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-2xl shadow-xl">
                <div>
                    <div className="mx-auto h-12 w-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                        <Lock className="text-white" size={24} />
                    </div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {t('login.title')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {t('login.subtitle')}
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit} autoComplete="off">
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded text-sm text-red-700">
                            {error}
                        </div>
                    )}
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div className="relative">
                            <Mail
                                className="absolute left-3 top-3 text-gray-400"
                                size={20}
                            />
                            <input
                                id="email-address"
                                name="email"
                                type="email"
                                autoComplete="off"
                                required
                                className="appearance-none rounded-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder={t('login.email')}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div className="relative">
                            <Lock
                                className="absolute left-3 top-3 text-gray-400"
                                size={20}
                            />
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="new-password"
                                required
                                className="appearance-none rounded-none relative block w-full px-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder={t('login.password')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
                        >
                            {isSubmitting ? (
                                <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                            ) : (
                                t('login.submit')
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Login;
